import { useState } from "react";
import { useDispatch } from "react-redux";
import { AppDispatch } from "configureStore";
import { Button, InlineStack, Banner, TextField } from "@shopify/polaris";
import { useTranslation } from "react-i18next";
import { useParams } from "react-router-dom";
import { setDestroy, setIsModalxOpen } from "components/modalx/modalx.slice";
import { fetchBusinessTripDetails } from "containers/business-trip-view-container/business-trip-container.slice";
import { annulBusinessTripReport } from "services/business-trip.service";

interface AnnulReportConfirmProps {
  tripId: string;
  companyId?: string;
}

const AnnulReportConfirm: React.FC<AnnulReportConfirmProps> = ({
  tripId,
  companyId: propCompanyId,
}) => {
  const dispatch: AppDispatch = useDispatch();
  const { t } = useTranslation();
  const { companyId: paramCompanyId } = useParams();
  const companyId = propCompanyId || paramCompanyId;

  const [errMsg, setErrMsg] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [isDone, setIsDone] = useState<boolean>(false);
  const [reason, setReason] = useState<string>("");

  const closeModal = () => {
    dispatch(setIsModalxOpen(false));
    dispatch(setDestroy());
  };

  const handleAnnul = async () => {
    if (!companyId || !tripId) return;
    try {
      setIsLoading(true);
      setErrMsg(null);
      await annulBusinessTripReport(companyId, tripId, reason);
      dispatch(fetchBusinessTripDetails({ tripId, companyId }));
      setIsLoading(false);
      // Keep the modal open with a clear instruction so it is obvious the
      // report was voided and the next step (regenerate + re-sign) is required.
      setIsDone(true);
    } catch (e: any) {
      setErrMsg(e?.response?.data?.message || "error");
      setIsLoading(false);
    }
  };

  // Success state: replace the confirmation UI with a prominent instruction and
  // drop the action button — leaving it would suggest nothing happened yet.
  if (isDone) {
    return (
      <>
        <Banner tone="success" title={t("report_annulled_success_title")}>
          <p>{t("report_annulled_success_instruction")}</p>
        </Banner>
        <div style={{ marginTop: "16px" }}>
          <InlineStack align="end">
            <Button variant="primary" onClick={closeModal}>
              {t("close")}
            </Button>
          </InlineStack>
        </div>
      </>
    );
  }

  return (
    <>
      <div className="form-warnings">
        {errMsg && (
          <div>
            <Banner tone="critical">{t(errMsg)}</Banner>
          </div>
        )}
      </div>
      <h1 style={{ textAlign: "center" }}>
        {t("are_you_sure_you_want_to_annul_report")}
      </h1>
      <div style={{ margin: "12px 0" }}>
        <Banner tone="warning">{t("annul_report_warning")}</Banner>
      </div>

      <div style={{ margin: "16px 0" }}>
        <TextField
          label={t("annul_report_reason")}
          value={reason}
          onChange={setReason}
          placeholder={t("annul_report_reason_placeholder")}
          autoComplete=""
          multiline={3}
          maxLength={2000}
          showCharacterCount
          requiredIndicator
        />
      </div>

      <InlineStack align="space-between">
        <Button
          tone="critical"
          onClick={handleAnnul}
          loading={isLoading}
          disabled={!reason.trim()}
        >
          {t("Yes")}
        </Button>
        <Button onClick={closeModal}>{t("No")}</Button>
      </InlineStack>
    </>
  );
};

export default AnnulReportConfirm;
